import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment";
import { toast } from "react-toastify";
import { FetchAPI } from "../../../../helpers/APIdata/fetchAPI";
import { getCustomerLookupAPI } from "../../../../helpers/fakebackend_helper";
import { publicPortalOrigin } from "../../../../helpers/publicPortalOrigin";

// Change #100251 - "we need a screen where we can issue a spin. It creates a
// unique code for the spin and once it's spun the go is over ... a list of
// spins (linked to customer, etc) and if it's been spun, the datetime and the
// outcome. We need the ability to void the spin as well."
//
// Issue a spin (optionally for a customer / email), copy its link, and see every
// spin issued for this spinner: when it was used, what it won, or void it while
// it is still unspent. Spending the code happens on the public spin itself.
const statusOf = (row) => {
  if (+row?.FlagVoid === 1) return { label: "Void", color: "default" };
  if (row?.SpunDateTime) return { label: "Spun", color: "success" };
  return { label: "Not spun", color: "warning" };
};

export default function LoyaltySpinnerIssues({ spinnerId, spinnerExternalId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [customerQuery, setCustomerQuery] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [qty, setQty] = useState(1);
  const [lastIssued, setLastIssued] = useState([]);

  const linkFor = (code) =>
    `${publicPortalOrigin()}/loyalty-reward-spinner/${spinnerExternalId}?spin=${code}`;

  const load = useCallback(async () => {
    if (!spinnerId) return;
    setLoading(true);
    try {
      const res = await FetchAPI(
        `/loyaltyspinnerissue/list?LoyaltySpinner_Id=${spinnerId}`,
        "GET"
      );
      setRows(res?.loyaltyspinnerissue_list || []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [spinnerId]);

  useEffect(() => {
    load();
    setLastIssued([]);
  }, [load]);

  // Customer lookup, as you type (3+ characters).
  useEffect(() => {
    const term = customerQuery.trim();
    if (term.length < 3 || (customer && term === customer.FullName)) {
      setCustomerOptions([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await getCustomerLookupAPI(term, 8);
        setCustomerOptions(res?.customer_list || []);
      } catch (e) {
        setCustomerOptions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [customerQuery, customer]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Link copied");
    } catch (e) {
      toast.info(text);
    }
  };

  const issue = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await FetchAPI("/loyaltyspinnerissue/create", "POST", [
        {
          LoyaltySpinner_Id: spinnerId,
          Customer_Id: customer?.Id || null,
          EmailAddress: email.trim() || null,
          Notes: notes.trim() || null,
          Qty: Math.max(1, Math.min(50, Number(qty) || 1)),
        },
      ]);
      if (Number(res?.status) !== 200) {
        toast.error(res?.message || "Could not issue the spin");
        return;
      }
      toast.success(res?.message || "Spin issued");
      setLastIssued(res?.loyaltyspinnerissue_list || []);
      setNotes("");
      setQty(1);
      load();
    } catch (e) {
      toast.error("Could not issue the spin");
    } finally {
      setSaving(false);
    }
  };

  const voidSpin = async (row) => {
    const reason = window.prompt(`Void spin ${row.Code}? Reason (optional):`, "");
    if (reason === null) return;
    try {
      const res = await FetchAPI("/loyaltyspinnerissue/void", "POST", [
        { Id: row.Id, VoidReason: reason || null },
      ]);
      if (Number(res?.status) !== 200) {
        toast.error(res?.message || "Could not void the spin");
        return;
      }
      toast.success("Spin voided");
      load();
    } catch (e) {
      toast.error("Could not void the spin");
    }
  };

  if (!spinnerId) {
    return (
      <Typography variant="body2" color="text.secondary">
        Save the spinner first, then you can issue spins for it.
      </Typography>
    );
  }

  return (
    <Box>
      {/* Issue */}
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        <Box sx={{ position: "relative" }}>
          <TextField
            label="Customer (optional)"
            size="small"
            fullWidth
            value={customerQuery}
            onChange={(e) => {
              setCustomerQuery(e.target.value);
              if (customer) setCustomer(null);
            }}
            placeholder="Type 3+ letters of a name, email or phone"
          />
          {customerOptions.length > 0 && (
            <Box
              sx={{
                position: "absolute",
                zIndex: 5,
                left: 0,
                right: 0,
                mt: 0.5,
                bgcolor: "background.paper",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                boxShadow: 3,
                maxHeight: 240,
                overflowY: "auto",
              }}
            >
              {customerOptions.map((c) => (
                <Box
                  key={c.Id}
                  sx={{ px: 1.5, py: 1, cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
                  onClick={() => {
                    setCustomer(c);
                    setCustomerQuery(c.FullName || `Customer #${c.Id}`);
                    if (!email && c.ContactEmailAddress) setEmail(c.ContactEmailAddress);
                    setCustomerOptions([]);
                  }}
                >
                  <Typography variant="body2" fontWeight={700}>
                    {c.FullName || `Customer #${c.Id}`}
                  </Typography>
                  {c.ContactEmailAddress && (
                    <Typography variant="caption" color="text.secondary">
                      {c.ContactEmailAddress}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <TextField
            label="Email (optional)"
            size="small"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            label="How many"
            size="small"
            type="number"
            inputProps={{ min: 1, max: 50 }}
            sx={{ width: 120 }}
            value={qty}
            onChange={(e) => setQty(e.target.value)}
          />
        </Stack>
        <TextField
          label="Notes (optional)"
          size="small"
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <Button variant="contained" color="success" onClick={issue} disabled={saving}>
          {saving ? "Issuing..." : Number(qty) > 1 ? `Issue ${qty} spins` : "Issue a spin"}
        </Button>

        {lastIssued.length > 0 && (
          <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: "success.light", color: "success.contrastText" }}>
            <Typography variant="body2" fontWeight={700} gutterBottom>
              Issued - send the link to whoever is spinning:
            </Typography>
            {lastIssued.map((i) => (
              <Stack key={i.Id} direction="row" alignItems="center" spacing={1}>
                <Typography variant="body2" sx={{ wordBreak: "break-all", flex: 1 }}>
                  {linkFor(i.Code)}
                </Typography>
                <Button size="small" variant="outlined" color="inherit" onClick={() => copy(linkFor(i.Code))}>
                  Copy
                </Button>
              </Stack>
            ))}
          </Box>
        )}
      </Stack>

      {/* List */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Issued spins {rows.length ? `(${rows.length})` : ""}
      </Typography>
      {loading && !rows.length ? (
        <Typography variant="body2" color="text.secondary">Loading...</Typography>
      ) : !rows.length ? (
        <Typography variant="body2" color="text.secondary">No spins issued yet.</Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <table className="table table-sm align-middle mb-0" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th>Code</th>
                <th>For</th>
                <th>Issued</th>
                <th>Spun</th>
                <th>Outcome</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const st = statusOf(r);
                const canVoid = !r.SpunDateTime && +r.FlagVoid !== 1;
                return (
                  <tr key={r.Id}>
                    <td style={{ fontFamily: "monospace" }}>{r.Code}</td>
                    <td>
                      {r.Customer_Id ? (
                        <a href={`/customer-single/${r.Customer_Id}/view`}>
                          {r.Customer_FullName || `Customer #${r.Customer_Id}`}
                        </a>
                      ) : (
                        r.EmailAddress || <span className="text-muted">-</span>
                      )}
                      {r.Notes && <div className="text-muted small">{r.Notes}</div>}
                    </td>
                    <td className="text-nowrap">
                      {r.CreatedDateTime ? moment(r.CreatedDateTime).format("DD/MM/YY HH:mm") : "-"}
                    </td>
                    <td className="text-nowrap">
                      {r.SpunDateTime ? moment(r.SpunDateTime).format("DD/MM/YY HH:mm") : "-"}
                    </td>
                    <td>{r.RewardLogic_RewardName || (r.SpunDateTime ? "-" : "")}</td>
                    <td>
                      <Tooltip title={+r.FlagVoid === 1 && r.VoidReason ? r.VoidReason : ""}>
                        <Chip size="small" label={st.label} color={st.color} />
                      </Tooltip>
                    </td>
                    <td className="text-nowrap text-end">
                      {canVoid && (
                        <>
                          <Tooltip title="Copy link">
                            <IconButton size="small" onClick={() => copy(linkFor(r.Code))}>
                              <i className="ri-link" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Void this spin">
                            <IconButton size="small" color="error" onClick={() => voidSpin(r)}>
                              <i className="ri-close-circle-line" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      )}
    </Box>
  );
}
